import AdvantageCode from '@gravity/advantage-code';
import { Catalog } from '@gravity/catalog';
import { EventCalendar } from '@gravity/event-calendar';
import { FrontEndError } from '@gravity/g-axios';
import Message, { MessageStatus } from '@gravity/message';
import ProductList from '@gravity/product-list';
import StylingConstants from '@gravity/styling-constants';
import { evolutionTheme } from '@gravity/theme-mapper';
import {
  DEFAULT_THEME_CONTEXT,
  ThemeContextType,
  TicketShopTheme,
  withThemeConsumer,
} from '@gravity/theme-provider';
import TsHeader from '@gravity/ts-header';
import {
  classNames,
  WithMediaQueriesType,
  withThemedStyles,
  withThemeQueries,
} from '@gravity/utils';
import WidgetState from '@gravity/widget-state';
import { CalendarModel, CalendarType } from '@nucleo/calendar';
import { CartType } from '@nucleo/cart';
import { CatalogModel, CatalogType } from '@nucleo/catalog';
import { ConfigType } from '@nucleo/config';
import { FilterType } from '@nucleo/filter';
import { ProductModel } from '@nucleo/product';
import { DefaultTabCodes, ProductListModel, ProductListType } from '@nucleo/product-list';
import { ResaleAvailabilityType } from '@nucleo/resale-availability';
import { SectionModel } from '@nucleo/section';
import StreamOnlineType from '@nucleo/stream-online';
import format from 'date-fns/format';
import getDate from 'date-fns/get_date';
import getMonth from 'date-fns/get_month';
import getYear from 'date-fns/get_year';
import get from 'lodash/get';
import size from 'lodash/size';
import PropTypes from 'prop-types';
import React, { Fragment, PureComponent } from 'react';
import { FormattedHTMLMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import App from './App';
import AppHelmet from './AppHelmet';
import { TsFooter } from './functional/TsFooter/src';
import ViewSelector from './functional/ViewSelector/src';
import './global-styles';

const { mediaQueries } = StylingConstants;

const PagesAvailable = ['list', 'calendar'];

const SKIP_LINK_AVAILABLE = ['catalog'];

const MAX_ITEM_PER_SECTION_FOR_REPORT = 50;

const Wrapper = withThemedStyles(
  styled.div.attrs({
    className: 'stx-TicketShopWidget',
  }),
  {
    [TicketShopTheme.EQUILIBRIUM]: css`
      margin: 0;
      padding: 0;
      border: 0;
      font-size: 100%;
      vertical-align: baseline;
      box-sizing: border-box;
      text-align: left;
      display: flex;
      flex-direction: column;
      min-height: 100vh;
      background: ${props => props.theme.bodyBgImage || 'none'};
      background-size: ${props => (props.theme.bgImageCover ? 'cover' : 'auto')};
      background-color: ${props => props.theme.bodyBgColor || 'transparent'};
      background-attachment: ${props =>
        props.theme.contentBgAttachment ? props.theme.contentBgAttachment : 'scroll'};

      .stx-ProductList-sectionProducts {
        margin: 1.5rem 6px;
        ${StylingConstants.mediaQueries.desktop`
        margin: 1.5rem 0;
      `};
      }
      .stx-ProductList-HOSPITALITY_BUNDLE {
        .stx-hospitalityProductLineTitle {
          font-size: 1.4rem;
        }
      }

      .stx-Section-HOSPITALITY_BUNDLE .stx-productDescription {
        .g-Button {
          padding: 0;
        }
      }
      .stx-Section-HOSPITALITY .stx-productDescription {
        .g-Button {
          padding: 0;
        }
      }
      p.semantic-no-styling {
        margin: 0;
      }
    `,
    [TicketShopTheme.EVOLUTION]: css`
      margin: 0;
      padding: 0;
      border: 0;
      font-size: 100%;
      vertical-align: baseline;
      box-sizing: border-box;
      text-align: left;
      display: flex;
      flex-direction: column;
      min-height: 100vh;
      background: ${props => props.theme.bodyBgImage || 'none'};
      background-size: var(--bgImageSize, 100vw auto);
      background-color: ${({ theme }) => theme.contentBgColor || 'transparent'};
      background-attachment: ${props =>
        props.theme.contentBgAttachment ? props.theme.contentBgAttachment : 'scroll'};

      .stx-Section-HOSPITALITY_BUNDLE .stx-productDescription {
        .g-Button {
          padding: 0;
        }
      }
      .stx-Section-HOSPITALITY .stx-productDescription {
        .g-Button {
          padding: 0;
        }
      }
      p.semantic-no-styling {
        margin: 0;
      }
    `,
  },
);

const WrapperView = withThemedStyles(styled.div, {
  [TicketShopTheme.EQUILIBRIUM]: css`
    margin-left: auto;
    margin-right: auto;
    width: 100%;
    ${mediaQueries.desktop`
      margin-top: 3rem;
      margin-bottom: 3rem;
    `};

    max-width: 1220px;
  `,
  [TicketShopTheme.EVOLUTION]: css`
    margin-left: auto;
    margin-right: auto;
    width: 100%;
    ${mediaQueries.desktop`
      margin-top: 0;
      margin-bottom: 3rem;
    `};

    max-width: ${evolutionTheme.breakpoints.xl}px;
  `,
});

const WrapperCatalog = withThemedStyles(
  styled(WrapperView).attrs({
    className: 'stx-TicketShopWidget-catalog',
    id: 'catalog',
    tabIndex: -1,
  }),
  {
    [TicketShopTheme.EQUILIBRIUM]: css`
      background-color: ${props =>
        (props.theme && props.theme.catalog && props.theme.catalog.backgroundColor) ||
        'transparent'};
    `,
    [TicketShopTheme.EVOLUTION]: css`
      background: transparent;
    `,
  },
);

const WrapperProductList = withThemedStyles(
  styled(WrapperView).attrs({
    className: 'stx-TicketShopWidget-ProductList',
    id: 'productList',
    tabIndex: -1,
  }),
  {
    [TicketShopTheme.EQUILIBRIUM]: css`
      padding-bottom: 1.5rem;
      margin-top: 1rem;

      .g-ViewSelector-header {
        padding: 0;
      }
    `,
    [TicketShopTheme.EVOLUTION]: css`
      > .g-ViewSelector {
        > div {
          > .g-ViewSelector-header {
            margin: 12px 0;
          }
        }
      }
    `,
  },
);

const WrapperCalendar = withThemedStyles(
  styled.div.attrs({
    className: `stx-TicketShopWidget-calendar`,
  }),
  {
    [TicketShopTheme.EQUILIBRIUM]: css`
      padding: 1.5rem 6px;

      ${mediaQueries.desktop`
        padding: 1.5rem 0;
    `};
    `,
    [TicketShopTheme.EVOLUTION]: css`
      ${mediaQueries.desktop`
        padding: 1.5rem 0;
      `};
      background-color: ${props => get(props, 'theme.contentBgColor', '#ffffff')};
      padding: ${evolutionTheme.spacings.xl}px;
      @media (max-width: 768px) {
        padding: 0;
      }
    `,
  },
);
const noResultsMessageColor = 'rgb(83, 75, 254)';
const NoResultsWrapper = withThemedStyles(styled(Message).attrs({
  className: 'stx-TicketShopWidget-noResultsContainer',
}),
  {
    [TicketShopTheme.EQUILIBRIUM]: css`
      padding: 2rem;
      border: none;
      color: ${noResultsMessageColor};
      .g-Icon {
        color: ${noResultsMessageColor};
      }
    `,
    [TicketShopTheme.EVOLUTION]: css`
      padding: 2rem;
      border: none;
      color: var(--messageInfoTitleColor, #6340ae);
      .g-Icon {
        color: var(--messageInfoTitleColor, #6340ae);
      }
    `,
  }
);

const NoResultsTitle = styled.div.attrs({
  className: 'stx-TicketShopWidget-noResults-title',
})`
  margin-bottom: 6px;
  font-weight: bolder;
`;

const MainPageHeader = styled.h1.attrs({
  className: 'accessibility-visually-hidden',
})`
  position: absolute;
  left: 0px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const SubPageHeader = styled.h1.attrs({
  className: 'accessibility-visually-hidden',
})`
  position: absolute;
  left: 0px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

/**
 * The catalog Widget display the product like the landing page for a specific point of sales
 */
class TicketShopWidget extends PureComponent {
  constructor(props) {
    super(props);
    this.menuRef = React.createRef();
    this.state = {
      page: this.getPage(null),
      isViewsDataLoaded: false,
    };
    this.handleAdvantageCodeEntered = this.handleAdvantageCodeEntered.bind(this);
    this.toggleMobileMenu = this.toggleMobileMenu.bind(this);
    this.changeLanguageFromMobile = this.changeLanguageFromMobile.bind(this);
  }

  componentDidMount() {
    const { page } = this.state;
    // Load the Catalog
    this.props.load();
    // Load the resale availability
    this.props.loadResaleAvailability();
    // Load the Cart
    this.props.loadCart();

    // Loading the rest of data if the URL contains a valid page (List of products or Calendar) to open it directly
    if (this.props.activateEnhancedFeature) {
      if (page === 'list' || page === 'calendar') {
        this.setState(
          {
            isViewsDataLoaded: true,
          },
          () => this.loadViewsData(),
        );
      }
    }

    // When the history has changed the state is updated
    window.onpopstate = () => this.reloadPage();
  }

  componentDidUpdate() {
    this.handlePageShow();
  }

  componentWillUnmount() {
    window.onpopstate = () => {};
  }

  /** Method to load the products for a section and tab */
  onLoadMore = (
    page,
    offset,
    tabCode,
    sectionCode,
    filterCriteria,
    filterChanged,
    customSectionDisplay,
    isReportContext,
  ) => {
    this.props.onLoadMore(
      page,
      offset,
      tabCode,
      sectionCode,
      filterCriteria,
      filterChanged,
      customSectionDisplay,
      isReportContext,
    );
  };

  /** Method to update filter state */
  onFilterReset = tabCode => {
    this.props.onFilterReset(tabCode);
  };

  /**
   * Method to update filter state
   */
  onFilterApplied = filterCriteria => {
    this.props.onFilterApplied(filterCriteria);
  };

  /**
   * Method to obtain the page and check if is valid or not to load it (valid pages are defined in PagesAvailable variable)
   */
  getPage = () => {
    let pageParam = window.location.hash.split('#')[1];
    if (SKIP_LINK_AVAILABLE.indexOf(pageParam) !== -1) {
      pageParam = null;
    }
    if (
      (pageParam && PagesAvailable.indexOf(pageParam) === -1) ||
      !this.props.activateEnhancedFeature
    ) {
      pageParam = null;
      window.location.hash = '';
    }
    if (!pageParam && this.props.config.defaultPage) {
      pageParam = this.props.config.defaultPage;
      window.location.hash = this.props.config.defaultPage;
    }
    return pageParam;
  };

  changeMonth = monthDate => {
    const { calendar, filterCalendar } = this.props;

    const monthName = format(monthDate, 'YYYY-MM');
    const currentFilterCriteria = get(filterCalendar, 'filterCriteria', null);

    // keep old state to compare with new state to decide to call service or not
    this.setState({
      [`${monthName}Criteria`]: currentFilterCriteria,
    });

    const prevFilterCriteria = get(this, `state[${monthName}Criteria]`, null);

    if (
      !calendar.sectionByMonth[monthName] ||
      (currentFilterCriteria && currentFilterCriteria !== prevFilterCriteria)
    ) {
      return this.props.loadMonth(getYear(monthDate), getMonth(monthDate));
    }
  };

  handleFilterCalendarReset = ({ month, detailedDay, filterCriteria }) =>
    this.props.onFilterCalendarReset({ month, detailedDay, filterCriteria });
  handleFilterCalendarApplied = ({ month, detailedDay, filterCriteria }) =>
    this.props.onFilterCalendarApplied({ month, detailedDay, filterCriteria });

  changeDetailedDay = day => {
    const monthName = format(day, 'YYYY-MM');
    const section = this.props.calendar.sectionByMonth[monthName];
    const cluster = SectionModel.getCluster(section, format(day, 'YYYY-MM-DD'));
    // get the quantity of occurrences to compare to the espected number of occurrences
    let quantityLoaded = 0;
    cluster.items.forEach(({ product }) => {
      quantityLoaded += ProductModel.getOccurrences(product).length;
    });
    if (cluster.quantity !== quantityLoaded) {
      return this.props.loadDay(getYear(day), getMonth(day), getDate(day));
    }
  };

  componentDidCatch(error, info) {
    // You can also log the error to an error reporting service
    this.props.logError(FrontEndError.RenderWidgetError, error, info);
  }

  /**
   * If a user enter a advantage code, redict him to the webpage of the ticketshop that handle advantages
   */
  handleAdvantageCodeEntered(value) {
    const { baseUrl } = this.props.config.configuration;
    window.location = `${baseUrl}list/partnerAdvantage?code=` + encodeURIComponent(`${value}`);
  }

  /**
   * open or hide the menu from depedning on the previous state
   */
  toggleMobileMenu() {
    if (this.menuRef.current) {
      this.menuRef.current.toggleMobileMenu();
    }
  }

  /**
   * change language and close the mobile menu
   */
  changeLanguageFromMobile(newLang) {
    this.toggleMobileMenu();
    return this.props.changeLanguage(newLang);
  }

  /**
   * Method to handle the history navigation and open a different page (Catalog, List Of Products or Calendar)
   */
  openPage = page => {
    const newPage = page ? `#${page}` : '/';
    window.scrollTo(0, 0);
    if (history.pushState) {
      history.pushState('', document.title, newPage);
    } else {
      location.hash = newPage;
    }
    this.reloadPage();
  };

  reloadPage = () => {
    const oldState = this.state;
    this.setState(
      prevState => ({
        ...prevState,
        page: this.getPage(),
      }),
      () => {
        if (this.state.page && !oldState.page && !this.state.isViewsDataLoaded) {
          this.setState(
            {
              isViewsDataLoaded: true,
            },
            () => this.loadViewsData(),
          );
        }
      },
    );
  };

  loadViewsData = () => {
    const tabDatas = get(this.props, 'productListTabsConfig.tabs', this.props.tabs);
    // Load the List of Products
    this.props.onLoadTabs(tabDatas);
    // Load the Calendar data
    this.props.loadMonths();
  };

  /**
   * When clicked Show all button, we hide the catalog and load the list of products
   */
  handleCatalogView = display => () => {
    this.openPage(display ? null : this.props.defaultView);
  };

  handleAdvantageValueEntered = value => {
    window.location =
      `${this.props.config.configuration.baseUrl}/list/partnerAdvantage?code=` +
      encodeURIComponent(`${value}`);
  };

  buildReportData = (config, productList, onProductSelectChange) => ({
    isReportContext: true,
    reportGenerateUrl: `https://${config.hostname}/report/generate`,
    reportVariants: config.reportVariants || [],
    seasonId: get(productList, 'seasons[0].id', null),
    requestConfig: {
      headers: config.headers,
      restHost: config.restHost,
    },
    onProductSelectChange,
  });

  /* Scroll to anchor when pageshow
  * Pageshow: an window event when the browser display document due to navigation (init loading, navigating between page from to page,
  * back and forward, etc)
  */
  handlePageShow = e => {
    const elId = window.location.hash;
    if (elId.length > 1) {
      const el = document.getElementById(elId.substr(1));
      if (el) el.scrollIntoView(true);
    }
  };

  getPageTitleByLocale = () => {
    return get(this.props.config, `config.pageTitle[${this.props.language}]`, '');
  };

  render() {
    const {
      config,
      isEmbedded,
      filter,
      productList,
      defaultLanguage,
      activateEnhancedFeature,
      activateCalendarEnhancedFeature,
      forceShowCodeInLanguageSelector,
      productListTabsConfig,
      arrCalendarFilters,
      onProductSelectChange,
      waitingListEnabled,
      useGoogleEnhancedTracking,
      skipLinks,
      contactItems,
      onWatchStreamOnline,
      streamOnline,
      updateStreamOnlineDialogVisibility,
      replaceProductNameByPerformance,
      useMainTitleBackgroundColor,
      widgetFWRootElement,
      useGA4,
      themeContext,
      themeQueries,
    } = this.props;

    const isEvolutionTheme = themeQueries.EVOLUTION;
    const isEquilibriumTheme = themeQueries.EQUILIBRIUM;

    let parsedSkipLinks = [];
    if (Array.isArray(skipLinks)) {
      parsedSkipLinks = skipLinks;
    }

    const customLinks = (config.configuration && config.configuration.customLinks) || [];
    const { user, productOwnerNumber, isReportContext } = config;
    const isSignedIn = Object.keys(user).length > 0;
    let productListView;
    let calendarView;
    let views = [];

    let userInfo = {};
    if (user) {
      const { contactNumber, hashEmail } = user;
      userInfo = { contactNumber, hashEmail };
    }
    const ecommerceGoogleTracking = {
      user: userInfo,
      useGoogleEnhancedTracking: useGoogleEnhancedTracking || false,
      useGA4: useGA4 || false,
    };

    // Check the number of columns for List of products and max number of items
    let { columnsInList, maxItemPerSectionInList } = this.props;

    columnsInList = get(
      this.props,
      `translations[${defaultLanguage}]['config.gravity.productList.columnsInList']`,
      columnsInList,
    );

    maxItemPerSectionInList = parseInt(
      get(
        this.props,
        `translations[${defaultLanguage}]['config.gravity.productList.maxItemPerSectionInList']`,
        maxItemPerSectionInList,
      ),
      10,
    );

    const listTabMode =
      productListTabsConfig &&
      productListTabsConfig.tabMode &&
      ['FLAT', 'BLOCK'].includes(productListTabsConfig.tabMode)
        ? productListTabsConfig.tabMode
        : this.props.listTabMode;

    const catalogData = this.props.catalog;
    const resaleAvailabilityData = this.props.resaleAvailabilityState.resaleAvailability;
    const productListData = this.props.productList;
    const hasProductList = ProductListModel.hasData(productListData);
    const calendarData = this.props.calendar;
    const hasMonthData =
      CalendarModel.hasData(calendarData) || CalendarModel.hasMonths(calendarData);
    const cartData = this.props.cart;

    const page = this.getPage();
    let data;
    if (page === 'list') {
      data = productListData;
    } else if (page === 'calendar') {
      data = calendarData;
    } else {
      data = catalogData;
    }

    const { LOADING, ERROR } = data;
    let curState;
    if (ERROR || this.props.configError) {
      curState = 'ERROR';
    } else if (
      LOADING &&
      (!page ||
        (page && ((page === 'list' && !hasProductList) || (page === 'calendar' && !hasMonthData))))
    ) {
      curState = 'LOADING';
    }
    const totalItemsCart = cartData.totalItems || 0;
    const urlSourceIframe = `${config &&
      config.configuration &&
      config.configuration.baseUrl}api/1/resources/custom/${config && config.language}/header.html`;

    const reportData = isReportContext
      ? this.buildReportData(config, productList, onProductSelectChange)
      : {};

    const seeAllButtonUrl = `https://${this.props.config.hostname}/list/events?lang=${config &&
      config.language}`;

    let pageView;

    if (page) {
      // If in report context => handle to display empty report available
      if (isReportContext && size(config.reportVariants) === 0) {
        pageView = (
          <div>
            <NoResultsWrapper status={MessageStatus.INFO} iconSize={themeQueries.EVOLUTION ? 'small' : 'xlarge'}>
              <NoResultsTitle>
                <FormattedHTMLMessage id="gravity.TicketShop.information" />
              </NoResultsTitle>
              <FormattedHTMLMessage id="gravity.ReportManagement.noReportManage" />
            </NoResultsWrapper>
          </div>
        );
      } else {
        const noProducts = curState !== 'LOADING' && (
          <div>
            <NoResultsWrapper status={MessageStatus.INFO} iconSize={themeQueries.EVOLUTION ? 'small' : 'xlarge'}>
              <NoResultsTitle>
                <FormattedHTMLMessage id="gravity.TicketShop.information" />
              </NoResultsTitle>
              <FormattedHTMLMessage id="gravity.TicketShop.noProductsAvailable" />
            </NoResultsWrapper>
          </div>
        );
        productListView =
          (hasProductList && (
            <ProductList
              themeContext={themeContext}
              onAdvantageValueEntered={this.handleAdvantageValueEntered}
              defaultTab={this.props.defaultTab}
              productList={productList}
              aspectRatio={this.props.aspectRatio}
              onLoadMore={this.onLoadMore}
              isSignedIn={isSignedIn}
              onTabChange={this.props.onTabChange}
              currencyCode={this.props.currencyCode}
              maxItemPerSection={
                config.isReportContext ? MAX_ITEM_PER_SECTION_FOR_REPORT : maxItemPerSectionInList
              }
              columns={columnsInList}
              displayActionBtn={this.props.displayActionBtn}
              showAdvantageCodeBar={false}
              tabMode={listTabMode}
              filter={filter}
              showPastDatesOnFilter={config.isReportContext}
              onFilterReset={this.onFilterReset}
              onFilterApplied={this.onFilterApplied}
              reportData={reportData}
              isLoading={LOADING}
              waitingListEnabled={waitingListEnabled}
              ecommerceGoogleTracking={ecommerceGoogleTracking}
              useMainTitleBackgroundColor={useMainTitleBackgroundColor}
              resaleAvailability={resaleAvailabilityData}
            />
          )) ||
          noProducts;

        calendarView =
          (activateEnhancedFeature &&
            hasMonthData && (
              <WrapperCalendar>
                <EventCalendar
                  detailedDay={calendarData && calendarData.firstDate}
                  month={
                    calendarData &&
                    this.props.getInitialMonth(calendarData.firstDate, calendarData.lastDate)
                  }
                  calendar={calendarData}
                  changeMonth={this.changeMonth}
                  changeDetailedDay={this.changeDetailedDay}
                  withTourOnly={activateCalendarEnhancedFeature}
                  filter={this.props.filterCalendar}
                  arrFilters={arrCalendarFilters}
                  onFilterReset={this.handleFilterCalendarReset}
                  onFilterApplied={this.handleFilterCalendarApplied}
                  showPastDatesOnFilter={!!productOwnerNumber}
                />
              </WrapperCalendar>
            )) ||
          noProducts;
        const viewsIcons = (() => {
          if (themeQueries.EVOLUTION) {
            return {
              menu: 'list',
              calendar: 'riCalendarEventLine',
            };
          }

          return {
            menu: 'menu',
            calendar: 'calendar',
          };
        })();
        views = [
          { id: 'list', icon: viewsIcons.menu, content: productListView },
          { id: 'calendar', icon: viewsIcons.calendar, content: calendarView },
        ];

        pageView = (
          <React.Fragment>
            {activateEnhancedFeature ? (
              <ViewSelector views={views} defaultView={page} onChange={this.openPage} />
            ) : (
              productListView
            )}
            <WidgetState state={curState} code={this.props.configErrorCode} />
          </React.Fragment>
        );
      }
    }

    return (
      <Wrapper>
        <App toggleMobileMenu={this.toggleMobileMenu} />
        <AppHelmet locale={this.props.language} />
        <Fragment>
          {!isEvolutionTheme &&
            !isEmbedded && (
              <Fragment>
                <TsHeader
                  user={user}
                  urlSourceIframe={urlSourceIframe}
                  config={config}
                  totalItemsCart={totalItemsCart}
                  customLinks={customLinks}
                  changeLocale={this.props.changeLanguage}
                  currentLocale={this.props.language}
                  showCartIndicator={!isReportContext}
                  displayLanguageCode={forceShowCodeInLanguageSelector}
                  contactItems={contactItems}
                  skipLinks={parsedSkipLinks}
                  defaultLanguage={this.props.defaultLanguage}
                  changeLanguageFromMobile={this.changeLanguageFromMobile}
                  innerRef={this.menuRef}
                />
              </Fragment>
            )}

          {widgetFWRootElement && (
            <WrapperCatalog
              className={classNames({
                'py-3': isEquilibriumTheme,
              })}>
              <div id={widgetFWRootElement} />
            </WrapperCatalog>
          )}

          {!widgetFWRootElement &&
            config.configuration.hasActivePartnerAdvantages && (
              <AdvantageCode
                themeContext={themeContext}
                onValueSubmit={this.handleAdvantageCodeEntered}
              />
            )}
          <MainPageHeader>{this.getPageTitleByLocale()}</MainPageHeader>
          {!widgetFWRootElement &&
            !page && (
              <WrapperCatalog
                className={classNames({
                  'py-3': isEquilibriumTheme,
                })}>
                <WidgetState state={curState} code={this.props.configErrorCode} />
                {CatalogModel.hasData(catalogData) && (
                  <Catalog
                    catalog={catalogData}
                    showSeeAllButton={this.props.showSeeAllButton}
                    onSeeAll={activateEnhancedFeature ? this.handleCatalogView(false) : null}
                    seeAllButtonUrl={seeAllButtonUrl}
                    currencyCode={this.props.currencyCode}
                    isSignedIn={isSignedIn}
                    waitingListEnabled={this.props.waitingListEnabled}
                    isLandingPage
                    onWatchStreamOnline={onWatchStreamOnline.bind(this.props)}
                    streamOnline={streamOnline}
                    updateStreamOnlineDialogVisibility={updateStreamOnlineDialogVisibility}
                    ecommerceGoogleTracking={ecommerceGoogleTracking}
                    replaceProductNameByPerformance={replaceProductNameByPerformance}
                    resaleAvailability={resaleAvailabilityData}
                  />
                )}
              </WrapperCatalog>
            )}
          {!widgetFWRootElement && page && <WrapperProductList>{pageView}</WrapperProductList>}
          {!isEvolutionTheme && !isEmbedded && <TsFooter config={config} />}
        </Fragment>
      </Wrapper>
    );
  }
}

TicketShopWidget.propTypes = {
  /** Global config : contains the point of sales that will provide information. To be passed in the STX.widgets.start function. */
  hostname: PropTypes.string.isRequired,
  /** Global config : contains the api key related to the point of sales. To be passed in the STX.widgets.start function. */
  apiKey: PropTypes.string.isRequired,
  /** Global config : Explicitely pass the language to the widget that will be loaded. If not passed, the default language of the institution will be used */
  language: PropTypes.string.isRequired,
  /** @IGNORE The default language of the system generated by the config load */
  defaultLanguage: PropTypes.string.isRequired,
  /** @IGNORE The parameter passed by the config to know if the page is embedded in mobile applicatin */
  isEmbedded: PropTypes.bool.isRequired,
  /** @IGNORE generated catalog after a load of data */
  catalog: CatalogType,
  /** @IGNORE generated resale availability after a load of data */
  resaleAvailabilityState: ResaleAvailabilityType,
  /** @IGNORE generated cart after a load of data */
  cart: CartType,
  /** @IGNORE generated function to load the catalog dated */
  load: PropTypes.func.isRequired,
  /** @IGNORE generated function to load the resale availability */
  loadResaleAvailability: PropTypes.func.isRequired,
  /** @IGNORE generated function to load the cart */
  loadCart: PropTypes.func.isRequired,
  /** @IGNORE generated function to change the language */
  changeLanguage: PropTypes.func.isRequired,
  /** Define if the button to redirect to the classic ticket shop should be shown */
  showSeeAllButton: PropTypes.bool,
  /** @IGNORE Contains the currency in which the price should be displayed, generated by config load */
  currencyCode: PropTypes.string.isRequired,
  /** @IGNORE generated, define if an error happend at the config level. */
  configError: PropTypes.bool,
  /** @IGNORE generated, define what kind of error happened at the config level */
  configErrorCode: PropTypes.string,
  /** @IGNORE point of sales configuration */
  waitingListEnabled: PropTypes.bool,
  /** @IGNORE generated function to log in case of error */
  logError: PropTypes.func.isRequired,
  /** @IGNORE Configuration object */
  config: ConfigType,
  /** @IGNORE the filter data */
  filterCalendar: PropTypes.objectOf(FilterType),
  /** @IGNORE the filter data */
  filter: PropTypes.objectOf(FilterType),
  /* The product list object, containing a list of sections */
  productList: ProductListType,
  /** @IGNORE generated function to handle load more product per section */
  onLoadMore: PropTypes.func,
  /** @IGNORE generated function to get all products when a filter is applied */
  onFilterApplied: PropTypes.func,
  /** @IGNORE generated function to get all products when a filter is applied */
  onFilterReset: PropTypes.func,
  /** @IGNORE generated function to get all products of calendar when a filter is applied */
  onFilterCalendarApplied: PropTypes.func,
  /** @IGNORE generated function to get all products of calendar when a filter is applied */
  onFilterCalendarReset: PropTypes.func,
  /** @IGNORE function to handle when a product is selected */
  onProductSelectChange: PropTypes.func,
  /** @IGNORE generated function to get initial month to display when init calendar widget */
  getInitialMonth: PropTypes.func,
  /** @IGNORE generated list of displayed tabs
   *  All supported tabs include: 'OFFERS', 'EVENTS', 'VISIT', 'SEASON_TICKETS', 'OTHER_PRODUCTS', 'HOSPITALITY' (optional), 'HOSPITALITY_BUNDLE' (optional), 'GOODS' (not implemented)
   */
  tabs: PropTypes.arrayOf(PropTypes.string),
  /** generated the default tab */
  defaultTab: PropTypes.oneOf(DefaultTabCodes),
  /** The Aspect ratio for the image. */
  aspectRatio: PropTypes.oneOf(['VERTICAL', 'HORIZONTAL', 'WIDE', 'SQUARE', 'LANDSCAPE']),
  /** @IGNORE generated function to handle changing tab */
  onTabChange: PropTypes.func,
  /* maximum product items per section in the List of Products view */
  maxItemPerSectionInList: PropTypes.number,
  /** Number of column contains product card on a row in the List of Products view */
  columnsInList: PropTypes.number,
  /** Option whether display action button or not */
  displayActionBtn: PropTypes.bool,
  /** True to use our gravity list of products instead of previous ticket-shop */
  activateEnhancedFeature: PropTypes.bool,
  /** @IGNORE generated function to load the tabs */
  onLoadTabs: PropTypes.func,
  /** @IGNORE generated calendar after a load of data */
  calendar: CalendarType,
  /** @IGNORE generated function to load the calendar months */
  loadMonths: PropTypes.func.isRequired,
  /** @IGNORE generated function to load the calendar months */
  loadMonth: PropTypes.func.isRequired,
  /** @IGNORE generated function to load a specific day */
  loadDay: PropTypes.func.isRequired,
  /** Id of the default view displayed */
  defaultView: PropTypes.string,
  /** The design of the tabs inside Product List. FLAT is more fresh and modern. BLOCK is more prominent. */
  listTabMode: PropTypes.oneOf(['FLAT', 'BLOCK']),
  /** List of translations */
  translations: PropTypes.object,

  productListTabsConfig: PropTypes.object,

  /** True to display Tour on the calendar widget */
  activateCalendarEnhancedFeature: PropTypes.bool,
  /** The list of supported filters must be displayed */
  arrCalendarFilters: PropTypes.arrayOf(PropTypes.string),
  /** True to use our gravity list of products instead of previous ticket-shop */
  forceShowCodeInLanguageSelector: PropTypes.bool,
  /** Option to use google enhance tracking to push data to data layer */
  useGoogleEnhancedTracking: PropTypes.bool,
  /** Function to handle watch stream online */
  onWatchStreamOnline: PropTypes.func,
  /** Stream Online object, contain dialog state and stream content */
  streamOnline: StreamOnlineType,
  updateStreamOnlineDialogVisibility: PropTypes.func,
  replaceProductNameByPerformance: PropTypes.bool,
  /* True to set a background color of tabs same as pos theme config */
  useMainTitleBackgroundColor: PropTypes.bool,
  /* The root for render new widget */
  widgetFWRootElement: PropTypes.any,
  useGA4: PropTypes.bool,
  themeContext: ThemeContextType,
  themeQueries: WithMediaQueriesType,
  currentTheme: PropTypes.string,
};

const emptyFn = () => {};

TicketShopWidget.defaultProps = {
  catalog: null,
  cart: null,
  resaleAvailabilityState: null,
  showSeeAllButton: false,
  configError: false,
  configErrorCode: null,
  waitingListEnabled: false,
  config: null,
  tabs: DefaultTabCodes,
  defaultTab: null,
  aspectRatio: 'HORIZONTAL',
  filter: null,
  displayActionBtn: true,
  columnsInList: 5,
  maxItemPerSectionInList: 10,
  calendar: undefined,
  activateEnhancedFeature: false,
  forceShowCodeInLanguageSelector: false,
  defaultView: 'list',
  listTabMode: 'FLAT',
  activateCalendarEnhancedFeature: false,
  filterCalendar: {},
  onTabChange: emptyFn,
  onLoadTabs: emptyFn,
  onLoadMore: emptyFn,
  onFilterApplied: emptyFn,
  onFilterReset: emptyFn,
  onFilterCalendarApplied: emptyFn,
  onFilterCalendarReset: emptyFn,
  onProductSelectChange: emptyFn(),
  getInitialMonth: emptyFn(),
  translations: {},
  arrCalendarFilters: [],
  useGoogleEnhancedTracking: false,
  onWatchStreamOnline: emptyFn,
  streamOnline: null,
  updateStreamOnlineDialogVisibility: emptyFn,
  replaceProductNameByPerformance: false,
  useMainTitleBackgroundColor: false,
  widgetFWRootElement: null,
  useGA4: false,
  themeContext: DEFAULT_THEME_CONTEXT,
  themeQueries: {},
  currentTheme: DEFAULT_THEME_CONTEXT.theme,
};

export default withThemeConsumer(withThemeQueries(TicketShopWidget));
